import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@redux/hooks";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericSearchbar, { ParsedFilter } from "@ui-components/Searchbar/GenericSearchbar";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { DataStateView } from "kontrolEdge/components/Backbone/BackboneDetails/StateViews";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import {
    OverviewReflector,
    OverviewCluster,
    BgpSession,
    useReflectorColumns,
    useClusterColumns,
    useBgpSessionColumns,
    filterRows,
} from "./OverviewData";
import {
    fetchOverview,
    shapeReflectors,
    shapeClusters,
    shapeBgpSessions,
    OverviewCounters,
    OverviewType,
} from "./request";

/* ------------------------------------------------------------------ */
/*  Summary Card                                                       */
/* ------------------------------------------------------------------ */

const SummaryCard: React.FC<{
    title: string;
    count: number;
    items: { label: string; count: number; variant: "success" | "error" | "warning" | "neutral" }[];
}> = ({ title, count, items }) => (
    <GenericCard isCollapsible={false}>
        <div className="p-4">
            <p className="text-xs text-semantic-text-secondary">{title}</p>
            <p className="text-3xl font-semibold text-semantic-text-primary mt-1">{count}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-semantic-text-secondary">
                {items.map((item) => (
                    <span key={item.label} className="flex items-center gap-1">
                        <span
                            className={`inline-block w-2 h-2 rounded-full ${
                                item.variant === "success"
                                    ? "bg-semantic-fg-success"
                                    : item.variant === "error"
                                    ? "bg-semantic-fg-danger"
                                    : item.variant === "warning"
                                    ? "bg-semantic-fg-warning"
                                    : "bg-semantic-fg-secondary"
                            }`}
                        />
                        {item.count} {item.label}
                    </span>
                ))}
            </div>
        </div>
    </GenericCard>
);

const EMPTY_COUNTERS: OverviewCounters = {
    reflectors: { total: 0, online: 0, offline: 0 },
    clusters: { total: 0, online: 0, degraded: 0, offline: 0 },
    bgp: { total: 0, established: 0, down: 0, idle: 0, noData: 0 },
};

const DEFAULT_ROWS_PER_PAGE = 10;
const MAX_FETCH_LIMIT = 50;

/* ------------------------------------------------------------------ */
/*  Main Page                                                          */
/* ------------------------------------------------------------------ */

const OverviewPage: React.FC = () => {
    const { tenantId } = useAppSelector((state) => state.selection);
    const { isAuthenticated, isLoading: isAuthLoading, getAccessTokenSilently } = useAuth0();
    const tokenRef = useRef(getAccessTokenSilently);
    tokenRef.current = getAccessTokenSilently;

    const [counters, setCounters] = useState<OverviewCounters>(EMPTY_COUNTERS);

    /* ---------- Table data ---------- */
    const [reflectors, setReflectors] = useState<OverviewReflector[]>([]);
    const [clusters, setClusters] = useState<OverviewCluster[]>([]);
    const [bgpSessions, setBgpSessions] = useState<BgpSession[]>([]);

    /* ---------- Loading / error ---------- */
    const [loadingReflectors, setLoadingReflectors] = useState(false);
    const [loadingClusters, setLoadingClusters] = useState(false);
    const [loadingBgp, setLoadingBgp] = useState(false);
    const [errorReflectors, setErrorReflectors] = useState<string | null>(null);
    const [errorClusters, setErrorClusters] = useState<string | null>(null);
    const [errorBgp, setErrorBgp] = useState<string | null>(null);

    /* ---------- Pagination state per table (server-side mode) ---------- */
    const [refPage, setRefPage] = useState(1);
    const [refRowsPerPage, setRefRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    const [clPage, setClPage] = useState(1);
    const [clRowsPerPage, setClRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    const [bgpPage, setBgpPage] = useState(1);
    const [bgpRowsPerPage, setBgpRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    /* ---------- Search filters (client-side) ---------- */
    const [refFilters, setRefFilters] = useState<ParsedFilter[]>([]);
    const [clFilters, setClFilters] = useState<ParsedFilter[]>([]);
    const [bgpFilters, setBgpFilters] = useState<ParsedFilter[]>([]);

    const hasRefSearch = refFilters.length > 0;
    const hasClSearch = clFilters.length > 0;
    const hasBgpSearch = bgpFilters.length > 0;

    /* ---------- Generic fetcher ---------- */
    const fetchSection = useCallback(
        async (
            type: OverviewType,
            limit: number,
            page: number,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            setData: (d: any) => void,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            shaper: (items: any[]) => any[],
            setLoading: (l: boolean) => void,
            setError: (e: string | null) => void,
        ) => {
            if (isAuthLoading || !isAuthenticated || !tenantId) return;
            setLoading(true);
            setError(null);
            try {
                const token = await tokenRef.current();
                const res = await fetchOverview(token, tenantId, type, limit, page);
                const data = res.data?.data ?? res.data;
                if (data?.counters) setCounters(data.counters);
                const items = data?.[type]?.items ?? data?.items ?? [];
                setData(shaper(items));
            } catch {
                setError(`Failed to load ${type}.`);
            } finally {
                setLoading(false);
            }
        },
        [isAuthenticated, isAuthLoading, tenantId],
    );

    /* ---------- Fetch wrappers ---------- */
    // When search is active: fetch all rows so client-side filter works across entire dataset
    // When no search: fetch only the current page
    const fetchReflectors = useCallback(
        () => {
            if (hasRefSearch) {
                return fetchSection("reflectors", MAX_FETCH_LIMIT, 1, setReflectors, shapeReflectors, setLoadingReflectors, setErrorReflectors);
            }
            return fetchSection("reflectors", refRowsPerPage, refPage, setReflectors, shapeReflectors, setLoadingReflectors, setErrorReflectors);
        },
        [fetchSection, refPage, refRowsPerPage, hasRefSearch],
    );

    const fetchClusters = useCallback(
        () => {
            if (hasClSearch) {
                return fetchSection("clusters", MAX_FETCH_LIMIT, 1, setClusters, shapeClusters, setLoadingClusters, setErrorClusters);
            }
            return fetchSection("clusters", clRowsPerPage, clPage, setClusters, shapeClusters, setLoadingClusters, setErrorClusters);
        },
        [fetchSection, clPage, clRowsPerPage, hasClSearch],
    );

    const fetchBgpSessions = useCallback(
        () => {
            if (hasBgpSearch) {
                return fetchSection("bgp", MAX_FETCH_LIMIT, 1, setBgpSessions, shapeBgpSessions, setLoadingBgp, setErrorBgp);
            }
            return fetchSection("bgp", bgpRowsPerPage, bgpPage, setBgpSessions, shapeBgpSessions, setLoadingBgp, setErrorBgp);
        },
        [fetchSection, bgpPage, bgpRowsPerPage, hasBgpSearch],
    );

    /* ---------- Refetch on pagination or search changes ---------- */
    useEffect(() => { fetchReflectors(); }, [fetchReflectors]);
    useEffect(() => { fetchClusters(); }, [fetchClusters]);
    useEffect(() => { fetchBgpSessions(); }, [fetchBgpSessions]);

    /* ---------- Pagination handlers ---------- */
    const handleRefPageChange = useCallback((p: number) => setRefPage(p), []);
    const handleRefRowsChange = useCallback((r: number) => { setRefRowsPerPage(r); setRefPage(1); }, []);

    const handleClPageChange = useCallback((p: number) => setClPage(p), []);
    const handleClRowsChange = useCallback((r: number) => { setClRowsPerPage(r); setClPage(1); }, []);

    const handleBgpPageChange = useCallback((p: number) => setBgpPage(p), []);
    const handleBgpRowsChange = useCallback((r: number) => { setBgpRowsPerPage(r); setBgpPage(1); }, []);

    /* ---------- Client-side filtered data (used when search is active) ---------- */
    const filteredReflectors = useMemo(() => filterRows(reflectors, refFilters), [reflectors, refFilters]);
    const filteredClusters = useMemo(() => filterRows(clusters, clFilters), [clusters, clFilters]);
    const filteredBgpSessions = useMemo(() => filterRows(bgpSessions, bgpFilters), [bgpSessions, bgpFilters]);

    /* ---------- Counter-based stats ---------- */
    const rc = counters.reflectors;
    const cc = counters.clusters;
    const bc = counters.bgp;

    const reflectorCols = useReflectorColumns();
    const clusterCols = useClusterColumns();
    const bgpSessionCols = useBgpSessionColumns();

    return (
        <div className="flex flex-col gap-4 p-1">
            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SummaryCard
                    title="Reflectors"
                    count={rc.total}
                    items={[
                        ...(rc.online > 0 ? [{ label: "online", count: rc.online, variant: "success" as const }] : []),
                        ...(rc.offline > 0 ? [{ label: "offline", count: rc.offline, variant: "error" as const }] : []),
                    ]}
                />
                <SummaryCard
                    title="Clusters"
                    count={cc.total}
                    items={[
                        ...(cc.online > 0 ? [{ label: "healthy", count: cc.online, variant: "success" as const }] : []),
                        ...(cc.degraded > 0 ? [{ label: "degraded", count: cc.degraded, variant: "warning" as const }] : []),
                        ...(cc.offline > 0 ? [{ label: "offline", count: cc.offline, variant: "error" as const }] : []),
                    ]}
                />
                <SummaryCard
                    title="BGP sessions"
                    count={bc.total}
                    items={[
                        ...(bc.established > 0 ? [{ label: "established", count: bc.established, variant: "success" as const }] : []),
                        ...((bc.idle ?? 0) > 0 ? [{ label: "idle", count: bc.idle ?? 0, variant: "neutral" as const }] : []),
                        ...(bc.down > 0 ? [{ label: "down", count: bc.down, variant: "error" as const }] : []),
                        ...((bc.noData ?? 0) > 0 ? [{ label: "no data", count: bc.noData ?? 0, variant: "neutral" as const }] : []),
                    ]}
                />
            </div>

            {/* Reflectors + Clusters tables side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Reflectors */}
                <GenericCard isCollapsible={false}>
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                        <h3 className="text-sm font-semibold text-semantic-text-primary">Reflectors</h3>
                        <GenericTag label={`${rc.total} total`} variant="neutral" size="small" rounded="full" />
                    </div>
                    <div className="px-4 pb-2">
                        <GenericSearchbar
                            placeholder="Find by attribute or value (case sensitive)..."
                            onFiltersChange={setRefFilters}
                        />
                    </div>
                    <DataStateView
                        loading={loadingReflectors}
                        error={errorReflectors}
                        isEmpty={!loadingReflectors && !errorReflectors && reflectors.length === 0}
                        onRetry={() => fetchReflectors()}
                        loadingContent={<TableSkeleton columns={5} rows={6} className="mx-4 mb-4" />}
                    >
                        {hasRefSearch ? (
                            <GenericTable<OverviewReflector>
                                columns={reflectorCols}
                                data={filteredReflectors}
                                rowKey={(row) => row.id}
                                initialRowsPerPage={refRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50]}
                                width="100%"
                                tableClassName="bg-semantic-bg-primary"
                                maxTableBodyHeight="400px"
                            />
                        ) : (
                            <GenericTable<OverviewReflector>
                                columns={reflectorCols}
                                data={reflectors}
                                rowKey={(row) => row.id}
                                serverSide
                                totalCount={rc.total}
                                currentPage={refPage}
                                rowsPerPage={refRowsPerPage}
                                onPageChange={handleRefPageChange}
                                onRowsPerPageChange={handleRefRowsChange}
                                rowsPerPageOptions={[10, 25, 50]}
                                loading={loadingReflectors}
                                width="100%"
                                tableClassName="bg-semantic-bg-primary"
                                maxTableBodyHeight="400px"
                            />
                        )}
                    </DataStateView>
                </GenericCard>

                {/* Clusters */}
                <GenericCard isCollapsible={false}>
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                        <h3 className="text-sm font-semibold text-semantic-text-primary">Clusters</h3>
                        <GenericTag label={`${cc.total} clusters`} variant="neutral" size="small" rounded="full" />
                    </div>
                    <div className="px-4 pb-2">
                        <GenericSearchbar
                            placeholder="Find by attribute or value (case sensitive)..."
                            onFiltersChange={setClFilters}
                        />
                    </div>
                    <DataStateView
                        loading={loadingClusters}
                        error={errorClusters}
                        isEmpty={!loadingClusters && !errorClusters && clusters.length === 0}
                        onRetry={() => fetchClusters()}
                        loadingContent={<TableSkeleton columns={5} rows={4} className="mx-4 mb-4" />}
                    >
                        {hasClSearch ? (
                            <GenericTable<OverviewCluster>
                                columns={clusterCols}
                                data={filteredClusters}
                                rowKey={(row) => row.id}
                                initialRowsPerPage={clRowsPerPage}
                                rowsPerPageOptions={[10, 25, 50]}
                                width="100%"
                                tableClassName="bg-semantic-bg-primary"
                                maxTableBodyHeight="400px"
                            />
                        ) : (
                            <GenericTable<OverviewCluster>
                                columns={clusterCols}
                                data={clusters}
                                rowKey={(row) => row.id}
                                serverSide
                                totalCount={cc.total}
                                currentPage={clPage}
                                rowsPerPage={clRowsPerPage}
                                onPageChange={handleClPageChange}
                                onRowsPerPageChange={handleClRowsChange}
                                rowsPerPageOptions={[10, 25, 50]}
                                loading={loadingClusters}
                                width="100%"
                                tableClassName="bg-semantic-bg-primary"
                                maxTableBodyHeight="400px"
                            />
                        )}
                    </DataStateView>
                </GenericCard>
            </div>

            {/* BGP sessions status — full width */}
            <GenericCard isCollapsible={false}>
                <div className="flex items-center justify-between px-4 pt-4 pb-2">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-semantic-text-primary">BGP sessions status</h3>
                        {bc.established > 0 && (
                            <GenericTag label={`${bc.established} up`} variant="success" size="small" rounded="full" />
                        )}
                        {(bc.idle ?? 0) > 0 && (
                            <GenericTag label={`${bc.idle} idle`} variant="neutral" size="small" rounded="full" />
                        )}
                        {bc.down > 0 && (
                            <GenericTag label={`${bc.down} down`} variant="error" size="small" rounded="full" />
                        )}
                    </div>
                    <GenericTag label={`${bc.total} total`} variant="neutral" size="small" rounded="full" />
                </div>
                <div className="px-4 pb-2">
                    <GenericSearchbar
                        placeholder="Find by attribute or value (case sensitive)..."
                        onFiltersChange={setBgpFilters}
                    />
                </div>
                <DataStateView
                    loading={loadingBgp}
                    error={errorBgp}
                    isEmpty={!loadingBgp && !errorBgp && bgpSessions.length === 0}
                    onRetry={() => fetchBgpSessions()}
                    loadingContent={<TableSkeleton columns={7} rows={4} className="mx-4 mb-4" />}
                >
                    {hasBgpSearch ? (
                        <GenericTable<BgpSession>
                            columns={bgpSessionCols}
                            data={filteredBgpSessions}
                            rowKey={(row) => row.id}
                            initialRowsPerPage={bgpRowsPerPage}
                            rowsPerPageOptions={[10, 25, 50]}
                            width="100%"
                            tableClassName="bg-semantic-bg-primary"
                            maxTableBodyHeight="400px"
                        />
                    ) : (
                        <GenericTable<BgpSession>
                            columns={bgpSessionCols}
                            data={bgpSessions}
                            rowKey={(row) => row.id}
                            serverSide
                            totalCount={bc.total}
                            currentPage={bgpPage}
                            rowsPerPage={bgpRowsPerPage}
                            onPageChange={handleBgpPageChange}
                            onRowsPerPageChange={handleBgpRowsChange}
                            rowsPerPageOptions={[10, 25, 50]}
                            loading={loadingBgp}
                            width="100%"
                            tableClassName="bg-semantic-bg-primary"
                            maxTableBodyHeight="400px"
                        />
                    )}
                </DataStateView>
            </GenericCard>
        </div>
    );
};

export default OverviewPage;
